/* eslint-disable react/prop-types */
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Menu,
  Modal,
  Progress,
  Row,
  Spin,
  notification,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import {
  descargarPlantillaSueldos,
  deleteHistorialSueldoPlaza,
  getHistorialesSueldoPlazaVistaOptimizada,
  importarSueldosPlaza,
  patchHistorialSueldoPlaza,
  postHistorialSueldoPlaza,
} from 'api/catalogos/historialSueldoPlaza';
import getProcesoEnCursoTerminado from 'api/catalogos/ProcesoEnCursoTerminado';
import { getPlazasvistaOptimized } from 'api/catalogos/plazas';
import { checkToken } from 'api/utils';
import CurrencyInput from 'components/CurrencyInput';
import Dragger from 'components/Dragger';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalConfirmation from 'components/ModalConfirmation';
import ModalDelete from 'components/ModalDelete';
import ReporteMovimientosModal from 'components/ReporteMovimientosModal';
import Select from 'components/Select';
import Table from 'components/Table';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import currencyFormatter from 'utils/currencyFormatter';
import exportarExcelDesdeColumnas from 'utils/exportarExcelDesdeColumnas';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import { ESTATUS_ACTIVO, ESTATUS_AUTORIZADO, ESTATUS_CANCELADO } from 'utils/constants';
import { getTiposDeMovimientoDePersonal } from 'api/catalogos/tiposDeMovimientoDePersonal';
import { FORMATO_MONEDA } from 'utils/excelReportes';

const sortHistorial = (list) => [...list].sort((a, b) => {
  const ea = a.estados_globales_id ?? 1;
  const eb = b.estados_globales_id ?? 1;
  if (ea !== eb) return ea - eb;
  return (b.fecha_inicial || '').localeCompare(a.fecha_inicial || '');
});

export const historialSueldoPlazaPermission = {
  permissionModel: 'historialsueldoplaza',
  catalogsModels: [],
  tabsModels: [],
};

const DESCRIPCION_PROCESO_IMPORTAR = 'Importar Sueldos Plaza';

const sueldoMensualRules = [
  { required: true, message: 'Requerido' },
  {
    validator: (_, value) => {
      if (value === undefined || value === null || value === '') {
        return Promise.resolve();
      }
      return Number(value) > 0
        ? Promise.resolve()
        : Promise.reject(new Error('El sueldo mensual debe ser un número positivo'));
    },
  },
];

const sueldoDiarioRules = [
  { required: true, message: 'Requerido' },
  ({ getFieldValue }) => ({
    validator(_, value) {
      if (value === undefined || value === null || value === '') {
        return Promise.resolve();
      }
      if (Number(value) <= 0) {
        return Promise.reject(new Error('El sueldo diario debe ser un número positivo'));
      }
      const sueldoMensual = getFieldValue('sueldo_mensual');
      if (
        sueldoMensual !== undefined && sueldoMensual !== null && sueldoMensual !== ''
        && Number(value) > Number(sueldoMensual)
      ) {
        return Promise.reject(new Error('El sueldo diario no puede ser mayor al sueldo mensual'));
      }
      return Promise.resolve();
    },
  }),
];

const HistorialSueldoPlaza = ({ permission }) => {
  const [form] = Form.useForm();
  const [formHistorial] = Form.useForm();
  const [formCarga] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [exportandoTodos, setExportandoTodos] = useState(false);
  const [showReporteSueldos, setShowReporteSueldos] = useState(false);
  const [plazas, setPlazas] = useState([]);
  const [data, setData] = useState([]);

  // selección de fila
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // visibilidad de modales
  const [visibleModal, setVisibleModal] = useState(false);
  const [visibleFormModal, setVisibleFormModal] = useState(false);
  const [visibleDelete, setVisibleDelete] = useState(false);
  const [visibleConfirmAutorizar, setVisibleConfirmAutorizar] = useState(false);
  const [visibleConfirmCancelar, setVisibleConfirmCancelar] = useState(false);
  const [visibleConfirmReactivar, setVisibleConfirmReactivar] = useState(false);
  const [tiposDeMovimientoDePersonal, setTiposDeMovimientoDePersonal] = useState([]);

  // ── Polling ───────────────────────────────────────────────────────────────
  const [isPollingImport, setIsPollingImport] = useState(false);
  const [progresoImport, setProgresoImport] = useState({ en_curso: 0, total: 0 });
  const pollingImportRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [_plazas, _tiposMovPersonal] = await Promise.all([
        getPlazasvistaOptimized(),
        getTiposDeMovimientoDePersonal({ estados_globales: ESTATUS_AUTORIZADO, modelo_origen: 'HISTORIAL_SUELDO' }),
      ]);
      if (mounted) {
        setPlazas(_plazas);
        setTiposDeMovimientoDePersonal(
          Array.isArray(_tiposMovPersonal)
            ? _tiposMovPersonal
            : _tiposMovPersonal?.results || [],
        );
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const reload = async () => {
    const plazaId = form.getFieldValue('plaza');
    if (!plazaId) return;
    const raw = await getHistorialesSueldoPlazaVistaOptimizada({ plaza_id: plazaId });
    const list = raw?.results || raw || [];
    setData(formatReceived(sortHistorial(list)));
  };

  // Polling useEffect — cada 2s hasta que procesando=false o máx ~11 min
  useEffect(() => {
    if (!isPollingImport) return () => {};

    let intentos = 0;
    const maxIntentos = 340;

    pollingImportRef.current = setInterval(async () => {
      try {
        intentos += 1;
        const lista = await getProcesoEnCursoTerminado({
          descripcion: DESCRIPCION_PROCESO_IMPORTAR,
        });
        const proceso = lista[0];

        if (proceso) {
          setProgresoImport({
            en_curso: proceso.registro_en_curso || 0,
            total: proceso.total_registros || 0,
          });
        }

        const termino = proceso && proceso.procesando === false;
        if (termino || intentos >= maxIntentos) {
          clearInterval(pollingImportRef.current);
          pollingImportRef.current = null;
          setIsPollingImport(false);
          setProgresoImport({ en_curso: 0, total: 0 });

          if (proceso?.resultado) {
            const byteCharacters = atob(proceso.resultado);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i += 1) {
              byteArray[i] = byteCharacters.charCodeAt(i);
            }
            const blob = new Blob([byteArray], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'HistorialSueldos_Resultado.xlsx';
            link.click();
            URL.revokeObjectURL(url);
          }

          notification.success({
            message: 'Importación completada',
            description: 'La carga masiva de Sueldos de Plaza finalizó. Se descargó el archivo de resultado.',
            placement: 'topRight',
            duration: 6,
          });
          await reload();
        }
      } catch (_err) {
        // No se interrumpe el polling: el siguiente tick reintenta. Si el
        // error es por sesión expirada, utils/api.js ya avisó y cerró
        // sesión; aquí solo dejamos rastro para diagnóstico.
        // eslint-disable-next-line no-console
        console.error('[polling importación historial de sueldos] error al consultar estado', _err);
      }
    }, 2000);

    return () => {
      if (pollingImportRef.current) {
        clearInterval(pollingImportRef.current);
        pollingImportRef.current = null;
      }
    };
  }, [isPollingImport]);

  const onChangePlaza = async (plazaId) => {
    setSelectedKeys([]);
    setSelectedRecord(null);
    if (plazaId) {
      const raw = await getHistorialesSueldoPlazaVistaOptimizada({ plaza_id: plazaId });
      const list = raw?.results || raw || [];
      setData(formatReceived(sortHistorial(list)));
    } else {
      setData([]);
    }
  };

  // ── Import Excel ────────────────────────────────────────────────────────────
  const onClickPlantilla = async () => {
    try {
      setLoading(true);
      await descargarPlantillaSueldos();
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    formCarga.resetFields();
    setVisibleModal(true);
  };

  const closeModal = () => {
    setVisibleModal(false);
    formCarga.resetFields();
  };

  const menuImport = (
    <Menu
      items={[
        {
          key: 1,
          label: 'Obtener Plantilla',
          icon: <FileExcelOutlined />,
          onClick: onClickPlantilla,
        },
        {
          key: 2,
          label: 'Cargar',
          icon: <DownloadOutlined />,
          onClick: openModal,
        },
      ]}
    />
  );

  const onUpload = async () => {
    try {
      await formCarga.validateFields();
      const { file } = formCarga.getFieldsValue();
      if (file) {
        setLoading(true);
        const response = await importarSueldosPlaza(file.file);
        if (response?.status === 202) {
          setVisibleModal(false);
          formCarga.resetFields();
          setProgresoImport({ en_curso: 0, total: 0 });
          setIsPollingImport(true);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  // ── CRUD ────────────────────────────────────────────────────────────────────
  const handleAdd = () => {
    setSelectedRecord(null);
    formHistorial.resetFields();
    setVisibleFormModal(true);
  };

  const handleEdit = () => {
    if (!selectedRecord) return;
    formHistorial.setFieldsValue({
      sueldo_diario: selectedRecord.sueldo_diario,
      sueldo_mensual: selectedRecord.sueldo_mensual,
      fecha_inicial: selectedRecord.fecha_inicial
        ? moment(selectedRecord.fecha_inicial) : null,
      fecha_final: selectedRecord.fecha_final
        ? moment(selectedRecord.fecha_final) : null,
      sueldo_retroactivo: selectedRecord.sueldo_retroactivo || false,
      tipo_de_movimiento: selectedRecord.tipo_de_movimiento || null,
    });
    setVisibleFormModal(true);
  };

  const onFinishHistorial = async () => {
    try {
      await formHistorial.validateFields();
    } catch {
      return;
    }
    const plazaId = form.getFieldValue('plaza');
    if (!plazaId) return;
    const values = formHistorial.getFieldsValue();
    const payload = {
      plaza: plazaId,
      sueldo_diario: values.sueldo_diario,
      sueldo_mensual: values.sueldo_mensual,
      fecha_inicial: values.fecha_inicial?.format('YYYY-MM-DD'),
      fecha_final: values.fecha_final?.format('YYYY-MM-DD') || null,
      sueldo_retroactivo: values.sueldo_retroactivo || false,
      tipo_de_movimiento: values.tipo_de_movimiento || null,
    };
    if (selectedRecord) {
      // Sin esto, el serializer (CustomModelSerializerMixin.validate) resetea
      // estados_globales a Activo cuando el campo no viene en el payload.
      payload.estados_globales = selectedRecord.estados_globales_id;
    }
    setLoading(true);
    const ok = selectedRecord
      ? await patchHistorialSueldoPlaza(selectedRecord.id, payload)
      : await postHistorialSueldoPlaza(payload);
    if (ok) {
      setVisibleFormModal(false);
      formHistorial.resetFields();
      setSelectedRecord(null);
      setSelectedKeys([]);
      await reload();
    }
    setLoading(false);
  };

  const confirmDelete = async () => {
    if (!selectedRecord) return;
    setLoading(true);
    const ok = await deleteHistorialSueldoPlaza(selectedRecord.id);
    if (ok) {
      setVisibleDelete(false);
      setSelectedRecord(null);
      setSelectedKeys([]);
      await reload();
    }
    setLoading(false);
  };

  // ── Autorizar / Cancelar ────────────────────────────────────────────────────
  const onConfirmAutorizar = async () => {
    setLoading(true);
    const ok = await patchHistorialSueldoPlaza(
      selectedRecord.id,
      { estados_globales: ESTATUS_AUTORIZADO },
    );
    if (ok) {
      setVisibleConfirmAutorizar(false);
      setSelectedRecord(null);
      setSelectedKeys([]);
      await reload();
    }
    setLoading(false);
  };

  const onConfirmCancelar = async () => {
    setLoading(true);
    const ok = await patchHistorialSueldoPlaza(
      selectedRecord.id,
      { estados_globales: ESTATUS_CANCELADO },
    );
    if (ok) {
      setVisibleConfirmCancelar(false);
      setSelectedRecord(null);
      setSelectedKeys([]);
      await reload();
    }
    setLoading(false);
  };

  const onConfirmReactivar = async () => {
    setLoading(true);
    const ok = await patchHistorialSueldoPlaza(
      selectedRecord.id,
      { estados_globales: ESTATUS_ACTIVO },
    );
    if (ok) {
      setVisibleConfirmReactivar(false);
      setSelectedRecord(null);
      setSelectedKeys([]);
      await reload();
    }
    setLoading(false);
  };

  // ── Columnas ────────────────────────────────────────────────────────────────
  const columns = [
    {
      titleText: 'Clave Plaza',
      dataIndex: 'plaza_clave',
      key: 'plaza_clave',
      width: 150,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'plaza_descripcion',
      key: 'plaza_descripcion',
      width: 300,
    },
    {
      titleText: 'Sueldo Diario',
      dataIndex: 'sueldo_diario',
      key: 'sueldo_diario',
      align: 'right',
      width: 160,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Sueldo Mensual',
      dataIndex: 'sueldo_mensual',
      key: 'sueldo_mensual',
      align: 'right',
      width: 160,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : 'Vigente'),
    },
    {
      titleText: 'Sueldo Retroactivo',
      dataIndex: 'sueldo_retroactivo',
      key: 'sueldo_retroactivo',
      width: 150,
      render: (val) => (val ? 'Sí' : 'No'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales_descripcion',
      key: 'estados_globales_descripcion',
      width: 130,
    },
  ];

  const onClickExportarTodos = async () => {
    setExportandoTodos(true);
    const raw = await getHistorialesSueldoPlazaVistaOptimizada();
    const list = raw?.results || raw || [];
    exportarExcelDesdeColumnas(
      formatReceived(sortHistorial(list)),
      columns,
      'Historial_de_Sueldos_de_Plazas',
    );
    setExportandoTodos(false);
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Form layout="vertical" form={form} name="general">
          <Row gutter={10}>
            <Col xs={24} sm={10}>
              <Form.Item name="plaza" label="No. de Plaza">
                <Select
                  keyProp="id"
                  labelProp="clave_y_descripcion"
                  dataSource={plazas}
                  onChange={onChangePlaza}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label=" ">
                <Button
                  icon={<FileExcelOutlined />}
                  loading={exportandoTodos}
                  onClick={onClickExportarTodos}
                >
                  Exportar Todos
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Table
          cols={columns}
          data={data}
          allowExportPDF={false}
          extraExportItems={[{
            key: 'reporte_cambios_sueldo',
            label: 'Reporte de Cambios de Sueldo',
            icon: <FileExcelOutlined />,
            onClick: () => setShowReporteSueldos(true),
          }]}
          permission={permission}
          allowCustomImport
          customImportMenu={menuImport}
          customImportText="Importar Historial de Sueldos"
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys, rows) => {
              setSelectedKeys(keys);
              setSelectedRecord(rows[0] || null);
            },
            type: 'radio',
          }}
          handleOnRowClick={(record) => {
            setSelectedKeys([record.id]);
            setSelectedRecord(record);
          }}
          controls={{
            onClickAdd: handleAdd,
            onClickEdit: handleEdit,
            onClickDelete: () => setVisibleDelete(true),
          }}
          disableEdit={selectedRecord?.estados_globales_id === ESTATUS_AUTORIZADO}
          customActions={[
            {
              permission: { model: 'historialsueldoplaza', action: 'cancelar' },
              onClick: () => { if (selectedRecord) setVisibleConfirmCancelar(true); },
              text: 'Cancelar',
              icon: <CloseCircleOutlined />,
              disabledBySelection: false,
              className: 'cancel',
              disabled: !selectedRecord
                || selectedRecord?.estados_globales_id === ESTATUS_CANCELADO,
            },
            {
              permission: { model: 'historialsueldoplaza', action: 'autorizar' },
              onClick: () => { if (selectedRecord) setVisibleConfirmAutorizar(true); },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRecord
                || selectedRecord?.estados_globales_id === ESTATUS_AUTORIZADO
                || selectedRecord?.estados_globales_id === ESTATUS_CANCELADO,
            },
            {
              permission: { model: 'historialsueldoplaza', action: 'reactivar' },
              onClick: () => { if (selectedRecord) setVisibleConfirmReactivar(true); },
              text: 'Reactivar',
              icon: <RedoOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRecord
                || selectedRecord?.estados_globales_id === ESTATUS_ACTIVO,
            },
          ]}
        />

        {/* Modal Add / Edit */}
        <Modal
          open={visibleFormModal}
          title={(
            <FormSubmitControls
              label={selectedRecord ? 'Editar Sueldo' : 'Agregar Sueldo'}
              onFinish={onFinishHistorial}
              onCancel={() => {
                setVisibleFormModal(false);
                formHistorial.resetFields();
              }}
              loading={loading}
            />
          )}
          footer={null}
          onCancel={() => {
            setVisibleFormModal(false);
            formHistorial.resetFields();
          }}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          destroyOnClose
        >
          <Form form={formHistorial} layout="vertical" onFinish={onFinishHistorial}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <CurrencyInput
                  name="sueldo_mensual"
                  label="Sueldo Mensual"
                  rules={sueldoMensualRules}
                  onChange={(val) => formHistorial.setFieldValue('sueldo_diario', (val / 30).toFixed(2))}
                />
              </Col>
              <Col xs={24} sm={12}>
                <CurrencyInput
                  name="sueldo_diario"
                  label="Sueldo Diario"
                  rules={sueldoDiarioRules}
                  dependencies={['sueldo_mensual']}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="fecha_inicial"
                  label="Fecha Inicial"
                  rules={[{ required: true, message: 'Requerido' }]}
                >
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="fecha_final" label="Fecha Final">
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="tipo_de_movimiento" label="Tipo de Movimiento de Personal">
                  <Select
                    labelProp="concepto"
                    keyProp="id"
                    dataSource={tiposDeMovimientoDePersonal}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="sueldo_retroactivo" valuePropName="checked" initialValue={false}>
                  <Checkbox>Sueldo Retroactivo</Checkbox>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal Eliminar */}
        <ModalDelete
          open={visibleDelete}
          onCancel={() => setVisibleDelete(false)}
          onDelete={confirmDelete}
          content={`el registro de sueldo del ${selectedRecord?.fecha_inicial || ''}`}
          loading={loading}
        />

        {/* Modal Autorizar */}
        <ModalConfirmation
          open={visibleConfirmAutorizar}
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleConfirmAutorizar(false)}
          contentTitle={`Autorizar el sueldo vigente desde ${selectedRecord?.fecha_inicial || ''}`}
          loading={loading}
        />

        {/* Modal Cancelar */}
        <ModalConfirmation
          open={visibleConfirmCancelar}
          onFinish={onConfirmCancelar}
          Confirm
          onCancel={() => setVisibleConfirmCancelar(false)}
          contentTitle={`Cancelar el sueldo vigente desde ${selectedRecord?.fecha_inicial || ''}`}
          loading={loading}
        />

        {/* Modal Reactivar */}
        <ModalConfirmation
          open={visibleConfirmReactivar}
          onFinish={onConfirmReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleConfirmReactivar(false)}
          contentTitle={`Reactivar el sueldo vigente desde ${selectedRecord?.fecha_inicial || ''}`}
          loading={loading}
        />

        {/* Modal Import Excel */}
        <Modal
          open={visibleModal}
          title={(
            <FormSubmitControls
              label="Importar Historial de Sueldos de Plazas"
              onFinish={onUpload}
              onCancel={closeModal}
              loading={loading}
            />
          )}
          onCancel={closeModal}
          footer={null}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          forceRender
          destroyOnClose
        >
          <Spin tip="Cargando..." spinning={loading}>
            <Form layout="vertical" form={formCarga} name="carga" onFinish={onUpload}>
              <Dragger
                label="Archivo"
                limitMB={30}
                rules={[{ required: true, message: 'El archivo es requerido' }]}
                name="file"
                allowedExtensions={['xlsx', 'xls']}
              />
            </Form>
          </Spin>
        </Modal>

        {/* Modal Progreso Importación */}
        <Modal
          open={isPollingImport}
          title="Importando Sueldos de Plaza..."
          footer={null}
          closable={false}
          maskClosable={false}
          centered
        >
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <Progress
              type="circle"
              percent={
                progresoImport.total > 0
                  ? Math.round((progresoImport.en_curso / progresoImport.total) * 100)
                  : 0
              }
            />
            <p style={{ marginTop: 16 }}>
              {progresoImport.en_curso}
              {' / '}
              {progresoImport.total}
              {' registros procesados'}
            </p>
          </div>
        </Modal>
        <ReporteMovimientosModal
          open={showReporteSueldos}
          onClose={() => setShowReporteSueldos(false)}
          tipo="sueldos"
          titulo="Reporte de Cambios de Sueldo"
          hojas={[{
            key: 'sueldos',
            nombre: 'Sueldos',
            titulo: 'CAMBIOS DE SUELDO DE PLAZAS',
            columnas: [
              {
                header: 'Sueldo Anterior Mensual', key: 'sueldo_mensual_anterior', width: 16, numFmt: FORMATO_MONEDA,
              },
              {
                header: 'Sueldo Nuevo Mensual', key: 'sueldo_mensual_nuevo', width: 16, numFmt: FORMATO_MONEDA,
              },
              {
                header: 'Sueldo Anterior Diario', key: 'sueldo_diario_anterior', width: 14, numFmt: FORMATO_MONEDA,
              },
              {
                header: 'Sueldo Nuevo Diario', key: 'sueldo_diario_nuevo', width: 14, numFmt: FORMATO_MONEDA,
              },
            ],
          }]}
          nota="Solo cambios reales de sueldo (sin la carga inicial ni los registros retroactivos). El empleado es quien ocupaba la plaza en la fecha del cambio."
          fileName="Reporte_Cambios_Sueldo"
        />
      </Spin>
    </Row>
  );
};

export default HistorialSueldoPlaza;
